import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../components/Shared/Button";
import Selectbox, { option } from "../../components/Shared/SelectBox";
import { toast } from "react-toastify";
import Spinner from "../../components/Shared/Spinner";
import { CreateIFRSQuestionResponseOption as CreateResponseOptionAPI } from "../../api/Quiz/CreateIFRSQuestionResponseOption";
import { GetIFRSQuestionById } from "../../api/Quiz/GetIFRSQuestionById";
import { ButtonTypes } from "../../utils/consts";
import { useUserDataStore } from "../../stores/user";
import { axios } from "../../lib/axios";

const CreateIFRSQuestionResponseOption = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { courseId, exam_id, question_id } = useParams<{
    courseId: string;
    exam_id: string;
    question_id: string;
  }>();
  const { userData } = useUserDataStore();
  const userRole = userData?.role;

  const [selectedType, setSelectedType] = useState<option | null>(null);
  const [answerFile, setAnswerFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);

  const typeOptions: option[] = [
    { id: "wordProcessor", name: "Word Processor" },
    { id: "spreadsheet", name: "Spreadsheet" },
  ];

  // Fetch question details
  const { isLoading: isQuestionLoading } = useQuery({
    queryFn: () => GetIFRSQuestionById(exam_id || "", question_id || ""),
    queryKey: ["ifrs-question", exam_id, question_id],
    enabled: !!exam_id && !!question_id && userRole !== "admin",
    refetchOnWindowFocus: false,
  });

  // File upload mutation for word processor files
  const { mutate: uploadFile } = useMutation({
    mutationFn: (formData: FormData) => {
      return axios.post(
        "/s3/upload?folderName=response_option_answers",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (event) => {
            if (event.total) {
              const progress = Math.round((event.loaded * 100) / event.total);
              setUploadProgress(progress);
            }
          },
        }
      );
    },
    onSuccess: (response) => {
      const fileUrl = response.data.url;
      setIsUploading(false);
      setUploadProgress(100);
      setUploadedFileUrl(fileUrl);
      toast.success("File uploaded successfully");
    },
    onError: () => {
      setIsUploading(false);
      setUploadProgress(0);
      toast.error("Failed to upload file");
    },
  });

  // Create response option mutation
  const { mutate: createResponseOption } = useMutation({
    mutationFn: (data: FormData) => {
      if (!question_id) throw new Error("Question ID is required");
      return CreateResponseOptionAPI(question_id, data);
    },
    onSuccess: (response) => {
      if (response.error) {
        setIsSubmitting(false);
        toast.error(
          response.error.message || "Failed to create response option"
        );
        return;
      }
      setIsSubmitting(false);
      toast.success("Response option created successfully");
      queryClient.invalidateQueries({
        queryKey: ["ifrs-question-response-options", question_id],
      });
      navigate(
        `/dashboard/courses/${courseId}/ifrs-exam/${exam_id}/questions/${question_id}/response-options`
      );
    },
    onError: () => {
      setIsSubmitting(false);
      toast.error("Failed to create response option");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedType) {
      toast.error("Please select a file type");
      return;
    }

    if (answerFile && isUploading) {
      toast.error("Please wait for file upload to complete");
      return;
    }

    setIsSubmitting(true);

    // For word processor files, use the uploaded file URL
    if (selectedType.id === "wordProcessor") {
      const formData = new FormData();
      formData.append("fileType", selectedType.id as string);
      if (uploadedFileUrl) {
        formData.append("answerFileUrl", uploadedFileUrl);
      }
      createResponseOption(formData);
    } else {
      // For spreadsheet files, use the existing flow
      const formData = new FormData();
      formData.append("fileType", selectedType.id as string);
      if (answerFile) {
        formData.append("file", answerFile);
      }
      createResponseOption(formData);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAnswerFile(file);

      // For word processor files, upload immediately
      if (selectedType?.id === "wordProcessor") {
        setIsUploading(true);
        setUploadProgress(0);
        setUploadedFileUrl(null);
        const fileFormData = new FormData();
        fileFormData.append("file", file);
        uploadFile(fileFormData);
      }
    }
  };

  const getAcceptedFiles = (): string => {
    if (selectedType?.id === "spreadsheet") {
      return ".xlsx,.xls,.csv";
    }
    if (selectedType?.id === "wordProcessor") {
      return ".docx,.doc";
    }
    return "";
  };

  if (isQuestionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <div className="flex items-center gap-4 mb-7">
        <Link
          to={`/dashboard/courses/${courseId}/ifrs-exam/${exam_id}/questions/${question_id}/response-options`}
          className="font-semibold text-primary hover:text-primary/80"
        >
          ← Back to Response Options
        </Link>
        <h1 className="text-burntBlack font-bold text-[32px]">
          Create Response Option
        </h1>
      </div>

      <div className="p-8 bg-white shadow rounded-xl border-black/15">
        <h2 className="mb-6 text-2xl font-bold">Response Option Information</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div className="w-full">
              <label className="block mb-3 text-lg font-semibold text-slate">
                File Type <span className="text-red-500">*</span>
              </label>
              <Selectbox
                selectOptions={typeOptions}
                selected={selectedType}
                setSelected={setSelectedType}
                placeholder="Select file type"
                className="!rounded"
              />
            </div>

            <div className="w-full">
              <label className="block mb-2 font-medium text-burntBlack">
                Answer File
              </label>
              <input
                type="file"
                name="answerFile"
                accept={getAcceptedFiles()}
                onChange={handleFileChange}
                disabled={!selectedType || isSubmitting}
                className="w-full p-3 transition-all border-2 border-black rounded-lg border-opacity-40 focus:outline-none hover:border-primary disabled:opacity-50"
              />
              {!selectedType && (
                <p className="mt-1 text-xs text-gray-500">
                  Please select a file type first
                </p>
              )}
              {answerFile && (
                <div className="mt-2 text-sm text-primary">
                  Selected file: {answerFile.name}
                </div>
              )}
              {isUploading && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1 text-sm">
                    <span className="text-gray-600">Uploading...</span>
                    <span className="font-semibold text-primary">
                      {uploadProgress}%
                    </span>
                  </div>
                  <div className="w-full h-2 overflow-hidden bg-gray-200 rounded-full">
                    <div
                      className="h-full transition-all duration-300 bg-primary"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type={ButtonTypes.SUBMIT}
                variant="primary"
                className="!h-12 w-40 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? <Spinner /> : "Create Response Option"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateIFRSQuestionResponseOption;
